import { MapPin, Video, X } from "lucide-react";
import type { JSX } from "react";

import type { AttendeeStatus, EventDTO } from "../../../common/api/types.js";
import { useModalFocus } from "../../../common/components/useModalFocus.js";

export function EventDetails({ event, busy, error, onClose, onDelete, onRespond }: {
  event: EventDTO;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onDelete: () => void;
  onRespond: (status: AttendeeStatus) => void;
}): JSX.Element {
  const panel = useModalFocus(onClose);
  const startsAt = new Date(event.startsAt);
  const endsAt = new Date(event.endsAt);

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center sm:p-6">
      <button type="button" aria-label="Termindetails schließen" onClick={onClose} className="absolute inset-0" />
      <section ref={panel} role="dialog" aria-modal="true" aria-labelledby="event-title" tabIndex={-1} className="relative w-full max-w-lg rounded-t-2xl border border-line bg-surface-raised p-5 pb-[calc(theme(spacing.5)+env(safe-area-inset-bottom))] shadow-2xl sm:rounded-2xl sm:pb-5">
        <div className="flex items-start gap-3">
          <span className="mt-1 h-12 w-1 shrink-0 rounded-full" style={{ backgroundColor: event.calendarColor }} />
          <div className="min-w-0 flex-1">
            <h2 id="event-title" className="text-lg font-semibold text-ink">{event.title}</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {event.allDay
                ? startsAt.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })
                : `${startsAt.toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} – ${endsAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`}
            </p>
            <p className="mt-1 text-xs text-ink-muted">{event.calendarName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Schließen" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"><X className="h-5 w-5" /></button>
        </div>

        {event.location ? <p className="mt-4 flex items-center gap-2 text-sm text-ink"><MapPin className="h-4 w-4 text-ink-muted" />{event.location}</p> : null}
        {event.meetingRoom ? <p className="mt-2 flex items-center gap-2 text-sm text-brand"><Video className="h-4 w-4" />Online-Meeting</p> : null}
        {event.description ? <p className="mt-4 whitespace-pre-wrap text-sm text-ink">{event.description}</p> : null}
        {event.attendees.length > 0 ? <div className="mt-4"><h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">Teilnehmer</h3><p className="text-sm text-ink">{event.attendees.map((attendee) => attendee.displayName).join(", ")}</p></div> : null}
        {error ? <p role="alert" className="mt-4 text-sm text-danger">{error}</p> : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-line pt-4">
          {event.myStatus === "INVITED" ? <><button type="button" disabled={busy} onClick={() => onRespond("DECLINED")} className="rounded-lg px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken disabled:opacity-50">Absagen</button><button type="button" disabled={busy} onClick={() => onRespond("ACCEPTED")} className="rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-ink disabled:opacity-50">Zusagen</button></> : null}
          {event.level !== "VIEW" ? <button type="button" disabled={busy} onClick={onDelete} className="mr-auto rounded-lg px-3 py-2 text-sm text-danger hover:bg-surface-sunken disabled:opacity-50">Löschen</button> : null}
        </div>
      </section>
    </div>
  );
}
